import { useState, useEffect } from "react";
import { ExternalLink, ArrowDown } from "lucide-react";
import { getPreviewType } from "services/utils";
import { ClipLoader } from "react-spinners";
import localization from "services/localization";
import SpecificAPI from "services/SpecificApi";

export default function ResourcePreview({ resource }) {
  const previewType = getPreviewType(resource);

  const [fetchingUrl, setFetchingUrl] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [signedUrl, setSignedUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!resource?.id || !resource?.school_id) return;

    let isMounted = true;

    const fetchSignedUrl = async () => {
      try {
        setFetchingUrl(true);
        setPreviewLoading(true);
        setError(false);
        setSignedUrl(null);

        const result = await SpecificAPI.getResourceURL(
          resource.id,
          resource.school_id,
        );

        const url = result?.["signed-url"];

        if (isMounted) {
          setSignedUrl(url);
        }
      } catch (err) {
        console.error("Failed to fetch signed URL", err);
        if (isMounted) setError(true);
      } finally {
        if (isMounted) setFetchingUrl(false);
      }
    };

    fetchSignedUrl();

    return () => {
      isMounted = false;
    };
  }, [resource?.id, resource?.school_id]);

  // 🔄 Fetching signed URL
  if (fetchingUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
        <ClipLoader size={40} color="var(--primary-icon)" />
        <p className="text-slate-500 text-sm">
          {localization.loading_preview || "Loading preview..."}
        </p>
      </div>
    );
  }

  // ❌ Error state
  if (error || !signedUrl) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16">
        <p className="text-slate-600">
          {localization.no_preview_available ||
            "Preview not available for this file type."}
        </p>
      </div>
    );
  }

  const overlayLoader = (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 z-10 gap-2">
      <ClipLoader size={30} color="var(--primary-icon)" />
      <span className="text-xs text-slate-600">
        {localization.loading_preview || "Loading preview..."}
      </span>
    </div>
  );

  // 🖼 IMAGE
  if (previewType === "image") {
    return (
      <div className="relative w-full h-[80vh]">
        {previewLoading && overlayLoader}
        <img
          src={signedUrl}
          alt={resource.originalName}
          className="max-w-full max-h-[80vh] object-contain mx-auto"
          onLoad={() => setPreviewLoading(false)}
        />
      </div>
    );
  }

  // 📄 PDF
  if (previewType === "pdf") {
    return (
      <div className="relative w-full h-[80vh]">
        {previewLoading && overlayLoader}
        <iframe
          src={signedUrl}
          title={resource.originalName}
          className="w-full h-full rounded"
          onLoad={() => setPreviewLoading(false)}
        />
      </div>
    );
  }

  // 📊 OFFICE FILES
  if (previewType === "office") {
    const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
      signedUrl,
    )}`;

    return (
      <div className="relative w-full h-[80vh]">
        {previewLoading && overlayLoader}

        <div className="absolute top-2 right-2 z-20">
          <a
            href={signedUrl}
            download={resource.originalName}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            <ArrowDown size={16} />
            {localization.download || "Download"}
          </a>
        </div>

        <iframe
          src={officeViewerUrl}
          title={resource.originalName}
          className="w-full h-full rounded"
          onLoad={() => setPreviewLoading(false)}
        />
      </div>
    );
  }

  // ❌ Unsupported
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16">
      <p className="text-slate-600">
        {localization.preview_not_available ||
          "Preview not available for this file type."}
      </p>
      <a
        href={signedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700"
      >
        <ExternalLink size={18} />
        {localization.open_in_new_tab || "Open in new tab"}
      </a>
    </div>
  );
}
